import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  type Timestamp,
  where,
} from 'firebase/firestore';
import type { ClientUsageStats, UsageLog, UsageStatsRollup } from '../types/usageStats';
import { firestore } from './firebase';

export const UsageStatsService = {
  async getMetrics(startDate: Date, endDate: Date, hideSystem: boolean = true) {
    // 1. Fetch Pricing Settings
    let costPerCredit = 0.15; // default fallback
    try {
      const pricingDoc = await getDoc(doc(firestore, 'platformSettings', 'pricing'));
      if (pricingDoc.exists() && pricingDoc.data()?.cost_per_credit_in_base) {
        costPerCredit = pricingDoc.data()?.cost_per_credit_in_base;
      }
    } catch (_e) {
      // ignore
    }

    // 2. Fetch Transactions from collectionGroup
    const q = query(
      collectionGroup(firestore, 'transactions'),
      where('timestamp', '>=', startDate),
      where('timestamp', '<=', endDate),
      orderBy('timestamp', 'desc')
    );

    const snap = await getDocs(q);

    const dailyMap: Record<string, UsageStatsRollup> = {};
    const clientMap: Record<string, ClientUsageStats> = {};
    const logs: UsageLog[] = [];
    let todayCost = 0;
    let todayTokens = 0;
    let todayRequests = 0;
    let totalMonthlyTokens = 0;

    const todayStr = new Date().toISOString().split('T')[0];

    // Helper to get client ID from transaction doc ref path
    // Path looks like: clients/{clientId}/billingLedger/main/transactions/{txId}
    const getClientIdFromRef = (ref: { path: string }): string => {
      const path = ref.path;
      if (path?.startsWith('clients/')) {
        return path.split('/')[1] || 'unknown';
      }
      return 'unknown';
    };

    for (const docSnap of snap.docs) {
      const data = docSnap.data();
      // Ignore manual topups
      if (data.action_type === 'manual_topup') {
        continue;
      }

      if (hideSystem) {
        if (
          data.action_type === 'rolling_cycle_reset' ||
          data.user_id === 'system' ||
          data.context_tag === 'system'
        ) {
          continue;
        }
      }

      const clientId = getClientIdFromRef(docSnap.ref as { path: string });
      const timestamp = data.timestamp?.toDate ? data.timestamp.toDate() : new Date();
      const dateStr = timestamp.toISOString().split('T')[0];
      const cost = (data.credits_burned || 0) * costPerCredit;
      const inputTokens = data.raw_input_tokens || 0;
      const outputTokens = data.raw_output_tokens || 0;

      // Daily aggregation
      if (!dailyMap[dateStr]) {
        dailyMap[dateStr] = {
          id: `daily_${dateStr}`,
          total_cost: 0,
          total_input_tokens: 0,
          total_output_tokens: 0,
          request_count: 0,
          last_updated: data.timestamp,
        };
      }
      dailyMap[dateStr].total_cost += cost;
      dailyMap[dateStr].total_input_tokens += inputTokens;
      dailyMap[dateStr].total_output_tokens += outputTokens;
      dailyMap[dateStr].request_count += 1;

      // Client aggregation
      if (!clientMap[clientId]) {
        clientMap[clientId] = {
          id: clientId,
          clientId,
          total_cost: 0,
          total_input_tokens: 0,
          total_output_tokens: 0,
          request_count: 0,
          last_updated: data.timestamp,
        };
      }
      clientMap[clientId].total_cost += cost;
      clientMap[clientId].total_input_tokens += inputTokens;
      clientMap[clientId].total_output_tokens += outputTokens;
      clientMap[clientId].request_count += 1;

      // KPI Aggregation
      if (dateStr === todayStr) {
        todayCost += cost;
        todayTokens += inputTokens + outputTokens;
        todayRequests += 1;
      }
      totalMonthlyTokens += inputTokens + outputTokens;

      // Logs collection (limit to first 1000 for UI performance)
      if (logs.length < 1000) {
        logs.push({
          id: docSnap.id,
          timestamp: data.timestamp,
          cost,
          model: data.model || 'Unknown',
          client_id: clientId,
          user_id: data.user_id || 'Unknown',
          context: data.context_tag || data.action_type || 'Unknown',
          input_tokens: inputTokens,
          output_tokens: outputTokens,
        });
      }
    }

    // Fetch client names
    const topClients = Object.values(clientMap)
      .sort((a, b) => b.total_cost - a.total_cost)
      .slice(0, 10);
    const clientsWithNames = await Promise.all(
      topClients.map(async (stat) => {
        try {
          const clientDoc = await getDoc(doc(firestore, 'clients', stat.clientId));
          return {
            ...stat,
            clientName: clientDoc.exists() ? clientDoc.data()?.clientName : undefined,
          };
        } catch (_e) {
          return stat;
        }
      })
    );

    const costTrend = Object.values(dailyMap).sort((a, b) => a.id.localeCompare(b.id));

    // Calculate monthly stats total cost (sum of all within range)
    const monthlyCost = costTrend.reduce((sum, day) => sum + day.total_cost, 0);
    const monthlyStats: UsageStatsRollup = {
      id: 'monthly',
      total_cost: monthlyCost,
      total_input_tokens: totalMonthlyTokens,
      total_output_tokens: 0,
      request_count: logs.length,
      last_updated: { toDate: () => new Date(), seconds: 0, nanoseconds: 0 } as Timestamp,
    };

    const todayStats: UsageStatsRollup = {
      id: 'today',
      total_cost: todayCost,
      total_input_tokens: todayTokens,
      total_output_tokens: 0,
      request_count: todayRequests,
      last_updated: { toDate: () => new Date(), seconds: 0, nanoseconds: 0 } as Timestamp,
    };

    return {
      todayStats,
      monthlyStats,
      costTrend,
      topClients: clientsWithNames,
      logs,
    };
  },

  async getHistoricalUsageData(targetClientId: string, subEnd: Date | number | string) {
    type UsageMapItem = {
      id: string;
      name: string;
      type: string;
      usage: { monthly: Record<string, Record<string, number>> };
    };
    const teamsMap: Record<string, UsageMapItem> = {};

    const usersMap: Record<string, UsageMapItem> = {};

    // Fetch Teams
    const { collection, getDocs, query, where } = await import('firebase/firestore');
    let teamsQuery: import('firebase/firestore').Query = collection(firestore, 'teams');
    if (targetClientId) {
      teamsQuery = query(teamsQuery, where('clientId', '==', targetClientId));
    }
    const teamsSnap = await getDocs(teamsQuery);
    for (const doc of teamsSnap.docs) {
      const d = doc.data() as Record<string, unknown>;
      teamsMap[doc.id] = {
        id: doc.id,
        name: (d.teamName as string) || doc.id,
        type: 'team',
        usage: { monthly: {} },
      };
    }

    // Fetch Users
    let usersQuery: import('firebase/firestore').Query = collection(firestore, 'users');
    if (targetClientId) {
      usersQuery = query(usersQuery, where('clientId', '==', targetClientId));
    }
    const usersSnap = await getDocs(usersQuery);
    for (const doc of usersSnap.docs) {
      const d = doc.data() as Record<string, unknown>;
      usersMap[doc.id] = {
        id: doc.id,
        name: (d.displayName as string) || (d.email as string) || doc.id,
        type: 'user',
        usage: { monthly: {} },
      };
    }

    let anchorDay = 1;
    if (subEnd) {
      let endDate: Date;
      if (typeof subEnd === 'object' && 'toDate' in subEnd && typeof subEnd.toDate === 'function') {
        endDate = subEnd.toDate();
      } else {
        endDate = new Date(subEnd as unknown as string | number);
      }
      if (!Number.isNaN(endDate.getTime())) {
        anchorDay = endDate.getUTCDate();
      }
    }

    const getCycleKeyForDate = (date: Date, anchorD: number) => {
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      let candidate = new Date(Date.UTC(year, month, Math.min(anchorD, lastDayOfMonth)));

      const jobDateStart = new Date(Date.UTC(year, month, date.getUTCDate()));
      if (jobDateStart > candidate) {
        const nextLastDay = new Date(Date.UTC(year, month + 2, 0)).getUTCDate();
        candidate = new Date(Date.UTC(year, month + 1, Math.min(anchorD, nextLastDay)));
      }
      const isoString = candidate.toISOString().split('T')[0];
      return isoString || '';
    };

    const mapContextToField = (tag: string): string | null => {
      if (!tag) {
        return null;
      }
      if (tag.includes('tender')) {
        return 'tenders';
      }
      if (tag.includes('draft')) {
        return 'drafts';
      }
      if (tag.includes('review')) {
        return 'reviews';
      }
      if (tag.includes('alignment') || tag.includes('report')) {
        return 'reports';
      }
      if (tag.includes('adhoc')) {
        return 'adhoc_documents';
      }
      if (tag.includes('cv')) {
        return 'generated_cvs';
      }
      if (tag.includes('chat')) {
        return 'section_chats';
      }
      if (tag.includes('people_cv')) {
        return 'team_searches';
      }
      if (tag.includes('workspace_initialization') || tag.includes('fee')) {
        return 'tender_go_fees';
      }
      return null;
    };

    if (targetClientId) {
      const jobsQuery = collection(firestore, `clients/${targetClientId}/billingLedger/main/jobs`);
      const jobsSnap = await getDocs(jobsQuery);
      for (const doc of jobsSnap.docs) {
        const job = doc.data();
        if (job.action_type === 'rolling_cycle_reset' || job.action_type === 'manual_topup') {
          continue;
        }
        if (job.isRefunded === true || job.status === 'refunded') {
          continue;
        }

        const startedAt = job.startedAt || job.timestamp;
        if (!startedAt) {
          continue;
        }

        const dateObj =
          typeof startedAt.toDate === 'function' ? startedAt.toDate() : new Date(startedAt);
        const cycleKey = getCycleKeyForDate(dateObj, anchorDay);
        if (!cycleKey) {
          continue;
        }

        const field = mapContextToField(job.context_tag || job.action_type);
        if (!field) {
          continue;
        }

        const teamId = job.team_id;
        const userId = job.user_id;

        if (teamId && teamsMap[teamId]) {
          const usage = teamsMap[teamId].usage;
          if (usage?.monthly) {
            if (!usage.monthly[cycleKey]) {
              usage.monthly[cycleKey] = {};
            }
            usage.monthly[cycleKey][field] = (usage.monthly[cycleKey][field] || 0) + 1;
          }
        }

        if (userId && usersMap[userId]) {
          const usage = usersMap[userId].usage;
          if (usage?.monthly) {
            if (!usage.monthly[cycleKey]) {
              usage.monthly[cycleKey] = {};
            }
            usage.monthly[cycleKey][field] = (usage.monthly[cycleKey][field] || 0) + 1;
          }
        }
      }
    }

    return [...Object.values(teamsMap), ...Object.values(usersMap)];
  },
};
